import { ComponentProps, FC } from 'react';

import { parse, DATE_FORMAT } from '@eneco/common/date';
import RichText from '@eneco/components/RichText/RichText';
import { spokenFieldHint } from '@eneco/components/spoken-field/spoken-field';
import { useFormatter } from '@eneco/i18n';
import { Checkbox, InputDatepicker, NotificationBox } from '@eneco/sparky';
import { Control, Controller, FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form';

export interface DeliveryDateFormFields {
  deliveryDate: string;
  consentConsiderationPeriod?: boolean;
}

interface DeliveryDateFieldTextualData {
  label?: string;
  placeholder?: string;
  hint?: string;
  /** Extra-field checkbox label template, with a `{startDate}` placeholder. */
  extraFieldLabel?: string;
}

interface DeliveryDatePickerProps {
  control: Control<DeliveryDateFormFields>;
  register: UseFormRegister<DeliveryDateFormFields>;
  setValue: UseFormSetValue<DeliveryDateFormFields>;
  errors: FieldErrors<DeliveryDateFormFields>;
  minimumDate: Date;
  maximumDate: Date;
  textualData?: DeliveryDateFieldTextualData;
  shouldShowExtraField: boolean;
  mutatedDeliveryDate: string;
}

/**
 * The delivery-date `Controller`+`InputDatepicker` and its optional consideration-period `Checkbox` —
 * byte-identical between the eneco and Oxxio delivery date steps, only the surrounding
 * urgency/notification logic differs per brand.
 */
export const DeliveryDatePicker: FC<DeliveryDatePickerProps> = ({
  control,
  register,
  setValue,
  errors,
  minimumDate,
  maximumDate,
  textualData,
  shouldShowExtraField,
  mutatedDeliveryDate,
}) => {
  const { format, date } = useFormatter();

  return (
    <>
      <Controller
        control={control}
        name="deliveryDate"
        render={({ field: { onChange, value, name } }) => (
          <InputDatepicker
            name={name}
            label={textualData?.label ?? ''}
            hint={spokenFieldHint(textualData?.hint, textualData?.placeholder)}
            error={errors.deliveryDate?.message}
            value={value}
            onDaySelect={d => onChange(d && date.short(d as Date))}
            onDayClick={() => {
              setValue('consentConsiderationPeriod', false);
            }}
            fromDate={minimumDate}
            toDate={maximumDate}
            defaultMonth={parse(value, DATE_FORMAT)}
            isReadOnly
          />
        )}
      />

      <div role="status" aria-live="polite" aria-atomic="true">
        {shouldShowExtraField ? (
          <Checkbox
            {...register('consentConsiderationPeriod')}
            label={format(textualData?.extraFieldLabel ?? '', {
              startDate: mutatedDeliveryDate?.toString(),
            })}
            error={errors.consentConsiderationPeriod?.message}
          />
        ) : null}
      </div>
    </>
  );
};

type MoveOutNotificationValue = {
  value?: { variant?: ComponentProps<typeof NotificationBox>['variant']; title?: string; content?: string };
};

interface DeliveryDateMovingNotificationTextualData {
  doesMoveOutNotification?: MoveOutNotificationValue;
  doesNotMoveOutNotification?: MoveOutNotificationValue;
}

interface DeliveryDateMovingNotificationProps {
  isMoving?: boolean;
  textualData?: DeliveryDateMovingNotificationTextualData;
}

/**
 * The moving/not-moving delivery date NotificationBox — byte-identical between the eneco and Oxxio
 * delivery date steps, only the condition under which it's shown differs per brand.
 */
export const DeliveryDateMovingNotification: FC<DeliveryDateMovingNotificationProps> = ({ isMoving, textualData }) => {
  const notification = isMoving
    ? textualData?.doesMoveOutNotification?.value
    : textualData?.doesNotMoveOutNotification?.value;

  if (!notification?.content) {
    return null;
  }

  return (
    <NotificationBox
      isAlert={false}
      headingLevel="h2"
      variant={notification.variant}
      title={notification.title}
      text={<RichText html={notification.content} />}
    />
  );
};
